import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const APP_CONFIG = {
  'qmax-guwahati': {
    name: 'QMAX Guwahati',
    type: 'Dealer',
    location: 'Guwahati',
    packageId: 'com.sanibiz.qmaxguwahati',
    isDealer: true,
  },
  'qmax-silchar': {
    name: 'QMAX Silchar',
    type: 'Dealer',
    location: 'Silchar',
    packageId: 'com.qmaxsilchar',
    isDealer: true,
  },
  'qmax-guwahati-salesperson': {
    name: 'QMAX Guwahati Salesperson',
    type: 'Salesperson',
    location: 'Guwahati',
    packageId: 'com.qmaxguwahati.salesperson',
    isDealer: false,
  },
  'qmax-silchar-salesperson': {
    name: 'QMAX Silchar Salesperson',
    type: 'Salesperson',
    location: 'Silchar',
    packageId: 'com.qmaxsilchar.salesperson',
    isDealer: false,
  },
};

const AppPrivacyPolicy = () => {
  const { appSlug } = useParams();
  const app = APP_CONFIG[appSlug];

  if (!app) return <Navigate to="/" replace />;

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <div className="pt-24 pb-16 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
            <div className="mb-8">
              <Link to="/" className="text-blue-600 hover:text-blue-700 font-medium">
                ← Back to Home
              </Link>
            </div>

            {/* App Badge */}
            <div className="mb-4">
              <span className="inline-block bg-blue-100 text-blue-700 text-sm font-semibold px-3 py-1 rounded-full">
                {app.name} — {app.type} App
              </span>
            </div>

            <h1 className="text-4xl font-bold text-gray-900 mb-2">Privacy Policy</h1>
            <p className="text-gray-500 mb-8">Last updated: March 2026</p>

            <div className="prose prose-lg max-w-none text-gray-700">

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Introduction</h2>
                <p className="mb-4">
                  QMAX Online ("we", "our", "us") respects your privacy and is committed to protecting your personal data.
                  This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use the
                  <strong> {app.name}</strong> {app.type} application (Package ID: <code>{app.packageId}</code>).
                </p>
                <p className="mb-4">
                  Please read this Privacy Policy carefully. By using the App, you consent to the practices described herein.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Information We Collect</h2>

                <h3 className="text-xl font-semibold text-gray-800 mb-3">2.1 Information You Provide</h3>
                <ul className="list-disc pl-6 mb-4 space-y-2">
                  <li><strong>Account Information:</strong> Name, email address, phone number, business name, and login credentials</li>
                  <li><strong>Business Information:</strong> Company name, GST number, business address, and {app.isDealer ? 'dealer registration details' : 'employee/staff details'}</li>
                  <li><strong>Transaction Data:</strong> Quote requests, order details, and communication with the {app.location} sales team</li>
                  <li><strong>Communication:</strong> Messages, feedback, and support requests</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-800 mb-3">2.2 Information Collected Automatically</h3>
                <ul className="list-disc pl-6 mb-4 space-y-2">
                  <li><strong>Device Information:</strong> Device type, operating system, unique device identifiers</li>
                  <li><strong>Usage Data:</strong> App usage patterns, features accessed, and session duration</li>
                  <li><strong>Log Data:</strong> IP address, access times, app crashes, and system activity</li>
                  <li><strong>Location Data:</strong> Approximate location based on IP address (we do not track precise GPS location)</li>
                </ul>

                {!app.isDealer && (
                  <div>
                    <h3 className="text-xl font-semibold text-gray-800 mb-3">2.3 Camera and QR Scanner Access</h3>
                    <p className="mb-4">
                      The {app.name} App requests camera access for QR code scanning functionality. This feature is used to:
                    </p>
                    <ul className="list-disc pl-6 mb-4 space-y-2">
                      <li>Scan product QR codes to retrieve tile information</li>
                      <li>Access inventory details quickly in the {app.location} warehouse</li>
                      <li>Capture product images for quote requests</li>
                    </ul>
                    <p className="mb-4">
                      Camera access is only used when you actively engage the scanner feature. Images are not stored on our
                      servers unless explicitly submitted as part of a request.
                    </p>
                  </div>
                )}

                {app.isDealer && (
                  <div>
                    <h3 className="text-xl font-semibold text-gray-800 mb-3">2.3 Camera Access</h3>
                    <p className="mb-4">
                      The {app.name} Dealer App may request camera access to allow you to capture images when submitting
                      quote requests or reporting product issues. Camera access is only used when you actively choose to
                      use this feature.
                    </p>
                  </div>
                )}
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">3. How We Use Your Information</h2>
                <p className="mb-4">We use the collected information to:</p>
                <ul className="list-disc pl-6 mb-4 space-y-2">
                  <li>Provide and maintain the {app.name} App services</li>
                  <li>Process quote requests and manage orders for the {app.location} region</li>
                  <li>Send you notifications about your account and orders</li>
                  <li>Communicate with you about products and services</li>
                  <li>Improve our Apps and develop new features</li>
                  <li>Ensure security and prevent fraud</li>
                  <li>Comply with legal obligations</li>
                  <li>Analyze usage patterns to enhance user experience</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Information Sharing</h2>
                <p className="mb-4">We may share your information with:</p>
                <ul className="list-disc pl-6 mb-4 space-y-2">
                  <li><strong>Affiliated Companies:</strong> For business operations and customer service</li>
                  <li><strong>Service Providers:</strong> Third parties who assist in operating our Apps and services</li>
                  <li><strong>Business Partners:</strong> Manufacturers and suppliers for order fulfillment in {app.location}</li>
                  <li><strong>Legal Authorities:</strong> When required by law or to protect our rights</li>
                </ul>
                <p className="mb-4">
                  We do not sell, rent, or trade your personal information to third parties for marketing purposes.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Data Security</h2>
                <p className="mb-4">
                  We implement appropriate technical and organizational measures to protect your personal data, including:
                </p>
                <ul className="list-disc pl-6 mb-4 space-y-2">
                  <li>Encrypted data transmission (HTTPS/SSL)</li>
                  <li>Secure server infrastructure</li>
                  <li>Access controls and authentication</li>
                  <li>Regular security audits and updates</li>
                  <li>Employee confidentiality agreements</li>
                </ul>
                <p className="mb-4">
                  However, no method of transmission over the internet is 100% secure. While we strive to protect your data,
                  we cannot guarantee absolute security.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Data Retention</h2>
                <p className="mb-4">
                  We retain your personal information for as long as your account is active or as needed to provide services.
                  We may also retain data as required for legal, accounting, or business purposes. When data is no longer needed,
                  it is securely deleted or anonymized.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Your Rights</h2>
                <p className="mb-4">You have the right to:</p>
                <ul className="list-disc pl-6 mb-4 space-y-2">
                  <li><strong>Access:</strong> Request a copy of your personal data</li>
                  <li><strong>Correction:</strong> Request correction of inaccurate data</li>
                  <li><strong>Deletion:</strong> Request deletion of your personal data (subject to legal requirements)</li>
                  <li><strong>Restriction:</strong> Request limitation of data processing</li>
                  <li><strong>Portability:</strong> Request transfer of your data to another service</li>
                  <li><strong>Objection:</strong> Object to certain types of data processing</li>
                </ul>
                <p className="mb-4">
                  To exercise these rights, please contact us using the information provided below.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Children's Privacy</h2>
                <p className="mb-4">
                  The {app.name} App is intended for business use and is not directed to individuals under 18 years of age.
                  We do not knowingly collect personal information from children. If you believe we have collected
                  information from a child, please contact us immediately.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Third-Party Links</h2>
                <p className="mb-4">
                  The App may contain links to third-party websites or services. We are not responsible for the privacy
                  practices of these third parties. We encourage you to review their privacy policies before providing
                  any personal information.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Updates to This Policy</h2>
                <p className="mb-4">
                  We may update this Privacy Policy from time to time. We will notify you of any significant changes
                  by posting the new policy in the App and updating the "Last updated" date. Your continued use of
                  the App after changes constitutes acceptance of the updated policy.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Contact Us</h2>
                <p className="mb-4">
                  If you have questions about this Privacy Policy or our data practices, please contact us:
                </p>
                <ul className="list-none space-y-2">
                  <li>📧 Email: <a href="mailto:info@starsolutions.biz" className="text-blue-600 hover:underline">info@starsolutions.biz</a></li>
                  <li>🌐 Website: <a href="https://qmaxonline.com" className="text-blue-600 hover:underline">qmaxonline.com</a></li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Grievance Officer</h2>
                <p className="mb-4">
                  In accordance with the Information Technology Act 2000 and rules made thereunder, the name and contact
                  details of the Grievance Officer are provided below:
                </p>
                <ul className="list-none space-y-2">
                  <li><strong>Name:</strong> Niraj Maheswari</li>
                  <li><strong>Email:</strong> <a href="mailto:info@starsolutions.biz" className="text-blue-600 hover:underline">info@starsolutions.biz</a></li>
                  <li><strong>Response Time:</strong> Within 30 days of receiving a complaint</li>
                </ul>
              </section>

            </div>

            {/* Link to Terms */}
            <div className="mt-8 pt-6 border-t border-gray-200">
              <p className="text-gray-600 text-sm">
                Also see our{' '}
                <Link to={`/${appSlug}/terms-of-use`} className="text-blue-600 hover:underline font-medium">
                  Terms of Use
                </Link>{' '}
                for the {app.name} App.
              </p>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default AppPrivacyPolicy;
